import Piano from '../piano/piano';
import SourcePoint from '../gdpr/sourcepoint.js';
import DebugHelper from '../helpers/debug-helper';
import GeaMetering from './metering';
import { adobeAnalytics } from '../adobe/adobe-analytics';

class GEA {
	constructor() {
		this.logger = new DebugHelper('GEA');
	}

	isActive() {
		if (typeof window.bi_gea !== 'undefined') {
			return true;
		}
		return false;
	}

	maybeInit() {
		this.logger.log('Maybe init');

		// Don't run if GEA is not active in settings
		if (!this.isActive() || !this.isAllowed()) {
			return;
		}

		// Don't run if the current URL is not a valid GEA URL
		if (!this.isValidUrl()) {
			return;
		}

		this.logger.log('Init');

		this.init();
	}

	async init() {
		let swgUrl = false;

		try {
			// Load the GEA scripts
			await this.loadScripts();

			// Get the swgUrl from Piano (url should be encoded)
			swgUrl = await Piano.GeaGetSwgUrl(
				encodeURIComponent(window.location.href),
			);

			// If swgUrl is successfully obtained, initialize geaMetering
			if (swgUrl) {
				GeaMetering.init(swgUrl);
			} else {
				console.error('LL GEA: No swgUrl found');
			}
		} catch (error) {
			console.error('LL GEA: One or more scripts failed to load: ', error);
		}
	}

	isAllowed() {
		if (!SourcePoint.vendorHasGrant('google-extended-access')) {
			return false;
		}

		return true;
	}

	async loadScripts() {
		this.logger.log('Load scripts');

		const urls = [
			'https://accounts.google.com/gsi/client',
			'https://news.google.com/swg/js/v1/swg.js',
			'https://news.google.com/swg/js/v1/swg-gaa.js',
		];

		const scriptPromises = urls.map(
			(url) =>
				new Promise((resolve, reject) => {
					// Check if the SDK is already loaded
					if (document.querySelector(`script[src="${url}"]`)) {
						resolve();
					}

					const script = document.createElement('script');
					script.type = 'text/javascript';
					script.async = true;
					script.src = url;

					script.onload = () => {
						this.logger.log('SDK loaded: ' + url);
						resolve();
					};

					script.onerror = () => {
						reject(new Error('LL GEA: Google GEA SDK could not be loaded.'));
					};

					const firstScript = document.getElementsByTagName('script')[0];
					firstScript.parentNode.insertBefore(script, firstScript);
				}),
		);

		// Wait for all scripts to load
		await Promise.all(scriptPromises);
	}

	/**
	 * Send GEA events to Adobe Analytics
	 * @see https://developers.google.com/news/subscribe/extended-access/integration-steps/analytics-integration?hl=en
	 */
	listenForEvents(subscriptions) {
		const self = this;

		this.logger.log('Start listening for events', subscriptions);

		if (
			typeof subscriptions === 'undefined' ||
			typeof subscriptions.getEventManager === 'undefined'
		) {
			return;
		}

		/**
		 * Run the client ready callback as proved by Google
		 * @see https://github.com/subscriptions-project/swg-js/blob/main/docs/embed-client.md#client-ready-callback
		 */
		subscriptions.getEventManager().then((manager) => {
			manager.registerEventListener((event) => {
				const eventName = self.getEventType(event.eventType);

				self.logger.log(
					`Subscriptions event fired: ${eventName} (${event.eventType})`,
					null,
					true,
				);

				// Send the event data to Adode Analytics
				adobeAnalytics.sendPianoGeaEvent(eventName, event.eventType);
			});
		});
	}

	getEventType(eventCode) {
		const eventTypes = {
			22: 'IMPRESSION_REGWALL',
			23: 'IMPRESSION_SHOWCASE_REGWALL',
			1060: 'ACTION_SHOWCASE_REGWALL_SIWG_CLICK',
			1035: 'ACTION_SHOWCASE_REGWALL_GSI_CLICK',
			1036: 'ACTION_SHOWCASE_REGWALL_EXISTING_ACCOUNT_CLICK',
			1024: 'ACTION_GET_ENTITLEMENTS',
			3011: 'EVENT_OFFERED_METER',
			21: 'IMPRESSION_METER_TOAST',
			3008: 'EVENT_UNLOCKED_BY_METER', // with eventOriginator: SWG_CLIENT
			1025: 'ACTION_METER_TOAST_SUBSCRIBE_CLICK',
			3007: 'EVENT_UNLOCKED_BY_SUSCRIPTION',
			3012: 'EVENT_UNLOCKED_FREE_PAGE',
			3009: 'EVENT_NO_ENTITLEMENTS',
			1: 'IMPRESSION_PAYWALL',
			3025: 'EVENT_SHOWCASE_METERING_INIT',
		};

		// Return the event type if it exists
		if (eventTypes[eventCode]) {
			return eventTypes[eventCode];
		}

		return 'UNKNOWN_EVENT_TYPE';
	}

	/*
	 * Validate if the URL params provided by GEA are valid.
	 * @see https://developers.google.com/news/subscribe/extended-access/reference/google-article-access-parameters?hl=en
	 */
	isValidUrl(url = null) {
		if (url === null) {
			url = window.location.href;
		}

		const params = ['gaa_at', 'gaa_ts', 'gaa_n', 'gaa_sig'];
		const foundParams = [];
		const parsedUrl = new URL(url);

		// Check if all required URL params are present
		for (let param of params) {
			const found = parsedUrl.searchParams.get(param);

			if (!found) {
				return false;
			}

			foundParams[param] = found;
		}

		// The `gaa_at` should be any of these values.
		if (!['la', 'g', 'na'].includes(foundParams['gaa_at'])) {
			return false;
		}

		// The `gaa_ts` URL param should contain a hex string timestamp which
		// points to the future.
		if (parseInt(foundParams['gaa_ts'], 16) < Date.now() / 1000) {
			return false;
		}

		// 1. Transform the gaa_sig value from its “web safe” format by replacing all - characters with +, and the _ characters with /.
		// let signature = foundParams['gaa_sig'];
		// signature = signature.replace('-', '+');
		// signature = signature.replace('_', '/');

		// 2. Base64 decode the transformed value from step 1.
		// signature = btoa(signature);

		// 3. Remove the gaa_sig URL parameter from the URL to form the data to verify.
		// let removedUrl = parsedUrl;
		// removedUrl.searchParams.delete('gaa_sig');

		// @TODO: Add validation of gaa_sig param (this is optional according to Google)
		// @see https://github.com/subscriptions-project/scenic-demo/blob/e23585fcd35c1daca5b0dd586c98287d8c7282c4/app/js/main.js#L362
		// 4. Loop through the latest JSON web keys (JWKs):
		// 4a. For production: https://play.google.com/newsstand/api/v3/articleaccess/publicsigningkey
		// 4b. For development: https://play.google.com/newsstand/api/v3/articleaccess/publicsigningkey/dev
		// 5. Verify the signature value from step 2 with the data from step 3 and the keys from step 4.

		return true;
	}
}

const instance = new GEA();
export default instance;
